/**
 * AdminBookingsPage ✦ iter224 — /admin/bookings
 *
 * The proper booking pipeline. Two parts on one screen:
 *
 *   1. "Pending enquiries" strip at the top — recent submissions
 *      from the website with a one-tap [Convert to Booking] action.
 *      Already-converted enquiries collapse to a small badge so
 *      Craig can't accidentally double-create.
 *
 *   2. "Bookings" list below — filterable by status, sorted by
 *      event_date ascending, each row inline-editable (status
 *      dropdown, equipment checkboxes, full edit modal). Statuses:
 *      enquiry · pending · confirmed · cancelled · completed.
 *      Only `confirmed`/`completed` count against inventory.
 *
 * Inventory deduction is computed live by /api/availability — no
 * counters need decrementing in this UI. When status flips
 * cancelled→confirmed, the customer surface updates on the next
 * request automatically.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import QuoteViewerModal from '../components/QuoteViewerModal';
import ExpiringQuotesStrip from '../components/ExpiringQuotesStrip';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const GOLD_HAIRLINE = 'rgba(212,170,82,0.14)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const STATUSES = [
  { key: 'enquiry',          label: 'Enquiry',          tint: 'rgba(125,181,140,0.28)', text: '#CFE6CC' },
  { key: 'pending',          label: 'Pending',          tint: 'rgba(226,199,123,0.30)', text: '#F4E2A8' },
  { key: 'awaiting_deposit', label: 'Awaiting Deposit', tint: 'rgba(227,154,79,0.34)',  text: '#F6CC92' },
  { key: 'confirmed',        label: 'Confirmed',        tint: 'rgba(125,181,140,0.42)', text: '#CFEFC6' },
  { key: 'completed',        label: 'Completed',        tint: 'rgba(212,170,82,0.30)',  text: '#F0DCA8' },
  { key: 'cancelled',        label: 'Cancelled',        tint: 'rgba(184,111,111,0.32)', text: '#E6B5B5' },
  { key: 'expired',          label: 'Expired',          tint: 'rgba(140,110,110,0.30)', text: '#D1A8A8' },
];

const STATUS_STYLE = STATUSES.reduce((acc, s) => { acc[s.key] = s; return acc; }, {});

const inputBase = {
  width: '100%',
  background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8, color: IVORY, fontFamily: SERIF, fontSize: 13.5,
  padding: '9px 12px', outline: 'none',
};

const labelStyle = {
  display: 'block', marginBottom: 5,
  fontFamily: SERIF, fontStyle: 'italic', fontSize: 10.5,
  letterSpacing: '0.18em', textTransform: 'uppercase', color: IVORY_GHOST,
};

const prettyDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [bookings, setBookings] = useState([]);
  const [counts, setCounts] = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);  // booking object or 'new'
  // iter234h — bulk selection state
  const [selected, setSelected] = useState(() => new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  // iter226 — pipeline counter strip + linked quote viewer
  const [pipeline, setPipeline] = useState(null);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [bookingQuotes, setBookingQuotes] = useState({});  // booking_id → quote_id

  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/bookings');
  }, [auth?.user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [bRes, eRes, iRes, pRes, summRes, qRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/enquiries`),
        axios.get(`${API}/admin/inventory`),
        axios.get(`${API}/services/photobooth/packages`),
        axios.get(`${API}/admin/pipeline-summary`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/quotes`).catch(() => ({ data: { quotes: [] } })),
      ]);
      setBookings(bRes.data.bookings || []);
      setCounts(bRes.data.counts || {});
      setEnquiries(eRes.data.enquiries || []);
      setInventory(iRes.data.items || []);
      setPackages(pRes.data.packages || []);
      setPipeline(summRes.data);
      // Map booking_id → most recent live (non-declined/expired) quote.
      const map = {};
      for (const q of (qRes.data?.quotes || [])) {
        if (q.booking_id && !['declined', 'expired'].includes(q.status)) {
          map[q.booking_id] = q.quote_id;
        }
      }
      setBookingQuotes(map);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Could not load bookings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const convert = async (enq) => {
    try {
      const r = await axios.post(`${API}/admin/enquiries/${enq.enquiry_id}/convert`);
      toast.success('Enquiry converted to booking ✦');
      await loadAll();
      setEditing(r.data.booking);  // open editor immediately so Craig can pick equipment
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed.');
    }
  };

  const saveBooking = async (payload, bookingId) => {
    try {
      if (bookingId) {
        await axios.patch(`${API}/admin/bookings/${bookingId}`, payload);
        toast.success('Booking updated ✦');
      } else {
        await axios.post(`${API}/admin/bookings`, payload);
        toast.success('Booking created ✦');
      }
      setEditing(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking? Inventory will restore for that date.')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success('Booking deleted ✦');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const sendDepositRequest = async (booking) => {
    if (!booking.deposit_amount) {
      const proceed = window.confirm(
        'No deposit amount set on this booking yet — open the editor to add deposit details first? (Cancel to send anyway.)'
      );
      if (proceed) { setEditing(booking); return; }
    }
    try {
      const r = await axios.post(`${API}/admin/bookings/${booking.booking_id}/send-deposit-request`);
      if (r.data?.email_sent === false) {
        toast.warning('Status updated to Awaiting Deposit, but the email did not send. Please contact the customer manually.');
      } else {
        toast.success('Deposit request sent ✦ Customer emailed.');
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send deposit request.');
    }
  };

  const markConfirmed = async (booking) => {
    try {
      await axios.patch(`${API}/admin/bookings/${booking.booking_id}`, { status: 'confirmed' });
      toast.success('Marked as Confirmed ✦ Inventory updated.');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark confirmed failed.');
    }
  };

  const generateOrViewQuote = async (booking) => {
    const existing = bookingQuotes[booking.booking_id];
    if (existing) { setViewingQuote(existing); return; }
    try {
      const r = await axios.post(`${API}/admin/bookings/${booking.booking_id}/generate-quote`);
      toast.success(`Draft quote ${r.data.quote.quote_number} created ✦`);
      await loadAll();
      setViewingQuote(r.data.quote.quote_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generate quote failed.');
    }
  };

  // iter232 — one-click publish from a completed booking. Imports
  // venue/customer/package/date/postcode, runs through /publish on
  // the server (auto-creates venue, geocodes, lands the pin), then
  // jumps to /admin/event-galleries with the new event selected so
  // Craig can drop in photos + review.
  const publishEventFromBooking = async (b) => {
    const msg = b.promoted_event_slug
      ? `This booking is already published as /events/${b.promoted_event_slug}. Open it now?`
      : `Publish "${b.customer_name || 'this booking'}" to the portfolio? The venue will be created, the postcode geocoded, and the map pin dropped automatically. You only need to add photos and a review afterwards.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.post(`${API}/admin/bookings/${b.booking_id}/publish-event`);
      const slug = r.data?.event?.slug;
      if (r.data?.already_promoted) {
        toast.info('Already published — opening editor ✦');
      } else {
        toast.success('Event published ✦ Add photos to finish.');
      }
      if (slug) {
        navigate(`/admin/event-galleries?slug=${slug}`);
      } else {
        await loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed.');
    }
  };

  const runAutoExpire = async () => {
    if (!window.confirm('Run the auto-expire sweep now? Awaiting-deposit bookings past their due date will be expired.')) return;
    try {
      const r = await axios.post(`${API}/admin/bookings/auto-expire-sweep`);
      toast.success(`Sweep complete · ${r.data.expired_count} expired.`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sweep failed.');
    }
  };

  // iter234m — one-click bulk heal of stranded "completed + public"
  // bookings that never got promoted to /events. Walks the entire
  // backlog server-side and fires the auto-promote gate per row.
  const [healing, setHealing] = useState(false);
  const runHealAllPins = async () => {
    if (!window.confirm('Heal every stranded booking? This walks all completed + public bookings that have a postcode but no map pin yet, and promotes them in one sweep. Idempotent — already-pinned bookings stay untouched.')) return;
    setHealing(true);
    try {
      const r = await axios.post(`${API}/admin/bookings/heal-all-pins`);
      const d = r.data || {};
      const ok = d.healed_count ?? 0;
      const seen = d.candidates_seen ?? 0;
      const ko = d.error_count ?? 0;
      toast.success(`✦ Heal sweep · ${ok} promoted of ${seen} candidate${seen === 1 ? '' : 's'}${ko ? ` · ${ko} failed` : ''}`, { duration: 8000 });
      if (ko && Array.isArray(d.errors) && d.errors.length) {
        // surface the first couple of errors so Craig can see why
        d.errors.slice(0, 3).forEach(err => {
          toast.error(`${err.booking_id}: ${err.reason}`, { duration: 9000 });
        });
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Heal sweep failed.');
    } finally {
      setHealing(false);
    }
  };

  // iter228 — one-shot import of "The Cube Booth Photobooths"
  // legacy external bookings into the iter224 pipeline.
  const importExternal = async () => {
    try {
      const preview = await axios.get(`${API}/admin/bookings/import-external/preview`);
      const { total, will_import, will_skip, will_fail } = preview.data;
      if (total === 0) {
        toast.info('No external bookings to import.');
        return;
      }
      const msg = `Import ${will_import} of ${total} external bookings into the EventDrop pipeline?` +
        (will_skip ? `\n\n${will_skip} already imported (will skip).` : '') +
        (will_fail ? `\n${will_fail} have bad event dates (will be flagged).` : '') +
        `\n\nImported bookings land as status = Confirmed. Originals stay in the supplier calendar as an audit trail.`;
      if (!window.confirm(msg)) return;
      const r = await axios.post(`${API}/admin/bookings/import-external`);
      toast.success(`Imported ${r.data.imported_count} · skipped ${r.data.skipped_count}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    }
  };

  const visible = useMemo(() => {
    if (filter === 'all') return bookings;
    return bookings.filter(b => b.status === filter);
  }, [bookings, filter]);

  // iter234h — bulk selection helpers
  const toggleSelected = useCallback((bid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(bid)) next.delete(bid); else next.add(bid);
      return next;
    });
  }, []);
  const selectVisibleNotReady = useCallback(() => {
    // Quick-pick: every visible booking whose map_status reports it's
    // not yet on the map. Saves Craig from ticking 23 rows by hand.
    setSelected(new Set(
      visible
        .filter(b => !(b.map_status?.is_on_map))
        .map(b => b.booking_id),
    ));
  }, [visible]);
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  const applyBulkPatch = useCallback(async (patch, label) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Apply "${label}" to ${ids.length} booking${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkRunning(true);
    try {
      const r = await axios.post(`${API}/admin/bookings/bulk-update`,
        { booking_ids: ids, patch },
      );
      const d = r.data || {};
      const ok = d.updated_count ?? 0;
      const ko = d.error_count ?? 0;
      toast.success(`✓ Updated ${ok}${ko ? ` · ${ko} failed` : ''}`, { duration: 6000 });
      clearSelection();
      await loadAll();
    } catch (err) {
      toast.error('Bulk update failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setBulkRunning(false);
    }
  }, [selected, clearSelection, loadAll]);

  const pendingEnquiries = useMemo(
    () => enquiries.filter(e => !e.converted),
    [enquiries]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <p style={{ fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', marginTop: 80 }}>
          Loading bookings ✦
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-bookings-page" style={pageStyle}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
          <div>
            <div style={eyebrowStyle}>✦ Bookings &amp; Pipeline</div>
            <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(26px, 4vw, 36px)', margin: 0, letterSpacing: '-0.015em' }}>
              Date-based booking control
            </h1>
            <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, fontSize: 13.5, margin: '8px 0 0', maxWidth: 640, lineHeight: 1.55 }}>
              Only <strong style={{ color: GOLD }}>Confirmed</strong> and <strong style={{ color: GOLD }}>Completed</strong> bookings deduct from inventory on the customer surface. Cancelling restores availability instantly.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={importExternal} data-testid="bookings-import-external"
              style={primaryPillStyle}>↗ Import external bookings</button>
            <button type="button" onClick={() => navigate('/dashboard/supplier/bookings')}
              data-testid="bookings-send-link"
              style={ghostPillStyle}>Send Booking Link ✦</button>
            <button type="button" onClick={() => navigate('/dashboard/supplier/calendar')}
              data-testid="bookings-create-external"
              style={ghostPillStyle}>+ Create External Booking</button>
            <button type="button" onClick={runAutoExpire} data-testid="bookings-auto-expire"
              style={{ ...ghostPillStyle, borderColor: 'rgba(184,111,111,0.4)', color: 'rgba(230,181,181,0.95)' }}>
              Run auto-expire sweep
            </button>
            <button type="button" onClick={runHealAllPins} data-testid="bookings-heal-all-pins"
              disabled={healing}
              style={{
                ...ghostPillStyle,
                borderColor: GOLD_FAINT,
                color: GOLD,
                opacity: healing ? 0.55 : 1,
                cursor: healing ? 'wait' : 'pointer',
              }}>
              {healing ? '✦ Healing pins…' : '✦ Heal all pins'}
            </button>
            <button type="button" onClick={() => navigate('/admin/quotes')} data-testid="bookings-quotes-link"
              style={ghostPillStyle}>Quotes archive →</button>
            <button type="button" onClick={() => setEditing('new')} data-testid="bookings-new"
              style={primaryPillStyle}>+ New booking</button>
            <button type="button" onClick={() => navigate('/admin')} data-testid="admin-back-link"
              style={ghostPillStyle}>← Admin home</button>
          </div>
        </header>

        {pipeline && (
          <section data-testid="bookings-pipeline-summary" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 10, marginBottom: 22,
          }}>
            {[
              { key: 'enquiry',          label: 'Enquiry' },
              { key: 'pending',          label: 'Pending' },
              { key: 'awaiting_deposit', label: 'Awaiting Deposit' },
              { key: 'confirmed',        label: 'Confirmed' },
              { key: 'completed',        label: 'Completed' },
              { key: 'cancelled',        label: 'Cancelled' },
              { key: 'expired',          label: 'Expired' },
            ].map(s => {
              const c = pipeline.counts?.[s.key] || 0;
              return (
                <div key={s.key}
                  data-testid={`pipeline-counter-${s.key}`}
                  style={{
                    padding: '14px 16px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}>
                  <div style={{ fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: IVORY_GHOST, marginBottom: 4 }}>
                    {s.label}
                  </div>
                  <div style={{ fontFamily: SERIF, fontSize: 22, color: c > 0 ? GOLD : IVORY_DIM, fontWeight: 500 }}>
                    {c}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* iter227 — chase strip: quotes due to expire within 7d */}
        <ExpiringQuotesStrip onView={(qid) => setViewingQuote(qid)} />

        {/* Pending enquiries strip */}
        {pendingEnquiries.length > 0 && (
          <section data-testid="bookings-enquiries-strip" style={{
            border: `1px dashed ${GOLD_FAINT}`, borderRadius: 12,
            padding: '20px 22px', marginBottom: 28,
            background: 'rgba(212,170,82,0.04)',
          }}>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, marginBottom: 12 }}>
              ✦ Pending enquiries · {pendingEnquiries.length}
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {pendingEnquiries.slice(0, 8).map((e) => (
                <div key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 14,
                    padding: '12px 14px', background: 'rgba(0,0,0,0.18)',
                    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10,
                  }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: SERIF, fontSize: 14.5, color: IVORY, fontWeight: 500 }}>
                      {e.name} <span style={{ color: IVORY_GHOST, fontStyle: 'italic', fontSize: 12.5 }}>
                        · {e.event_date} {e.venue ? `· ${e.venue}` : ''}
                      </span>
                    </div>
                    <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_DIM, letterSpacing: '0.02em' }}>
                      {e.email} · {e.phone}{e.event_type ? ` · ${e.event_type}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => convert(e)}
                    data-testid={`enquiry-convert-${e.enquiry_id}`}
                    style={{
                      ...primaryPillStyle, padding: '7px 14px', fontSize: 12.5,
                    }}>Convert to Booking ✦</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* iter234g — Map readiness help banner. Answers "what does
            a booking need to drop a pin + show a photo on /venues?" */}
        <details data-testid="map-readiness-help" style={{
          margin: '0 0 18px', padding: '12px 16px',
          background: 'rgba(212,170,82,0.06)',
          border: '1px solid rgba(212,170,82,0.22)',
          borderRadius: 12,
        }}>
          <summary style={{
            cursor: 'pointer', fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13.5, color: GOLD, listStyle: 'none',
            letterSpacing: '0.04em',
          }}>
            ✦ How do I get a booking to pin on the map with a photo?
          </summary>
          <div style={{
            marginTop: 12, fontFamily: SERIF, fontSize: 13.5,
            color: IVORY_DIM, lineHeight: 1.65,
          }}>
            Every booking needs <strong style={{ color: IVORY }}>four
            things</strong> to land on <em>/venues</em>:
            <ol style={{ margin: '8px 0 12px 22px', padding: 0 }}>
              <li><strong style={{ color: IVORY }}>Postcode</strong>
                — a real UK postcode (we geocode it via postcodes.io).</li>
              <li><strong style={{ color: IVORY }}>Mark as public event</strong>
                {' '}— tick the &ldquo;Show this booking on the public
                map / portfolio&rdquo; toggle in the edit modal.</li>
              <li><strong style={{ color: IVORY }}>Status =
                Completed</strong> — the moment you flip status to
                Completed, the booking auto-promotes to /events with
                a pin on the map.</li>
              <li><strong style={{ color: IVORY }}>Hero photo</strong>
                {' '}— for the pin to show a thumbnail (instead of a
                plain coloured dot), open the linked event on{' '}
                <a href="/admin/historic-events" style={{
                  color: GOLD, textDecoration: 'underline',
                }}>/admin/historic-events</a>{' '}and upload a hero
                image. CSV imports can include <code>hero_image_url</code>{' '}
                directly.</li>
            </ol>
            Each row below shows a small badge: <strong style={{
              color: '#9ED39E',
            }}>✓ Pinned · Photo</strong> means it&apos;s on the map
            with a thumbnail; <strong style={{ color: GOLD }}>◐
            Pinned · No photo</strong> means the pin is there but
            it&apos;s a plain dot; <strong style={{ color: '#E39A4F' }}>⚠
            N to do</strong> means it&apos;s not on the map yet —
            hover the badge to see exactly what&apos;s missing.
          </div>
        </details>

        {/* Status filter */}
        <div data-testid="bookings-filter-strip" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
          <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} testid="filter-all">
            All · {bookings.length}
          </FilterPill>
          {STATUSES.map((s) => (
            <FilterPill key={s.key}
              active={filter === s.key}
              onClick={() => setFilter(s.key)}
              testid={`filter-${s.key}`}>
              {s.label} · {counts[s.key] || 0}
            </FilterPill>
          ))}
        </div>

        {/* Bookings list */}
        {visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM }}>
            No bookings to show. Add one with <span style={{ color: GOLD }}>+ New booking</span> above or convert a pending enquiry.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visible.map((b) => (
              <BookingRow
                key={b.booking_id}
                booking={b}
                packages={packages}
                quoteId={bookingQuotes[b.booking_id]}
                selected={selected.has(b.booking_id)}
                onToggleSelect={() => toggleSelected(b.booking_id)}
                onEdit={() => setEditing(b)}
                onDelete={() => deleteBooking(b.booking_id)}
                onStatusChange={(status) => saveBooking({ status }, b.booking_id)}
                onSendDeposit={() => sendDepositRequest(b)}
                onMarkConfirmed={() => markConfirmed(b)}
                onQuote={() => generateOrViewQuote(b)}
                onPublishEvent={() => publishEventFromBooking(b)}
              />
            ))}
          </div>
        )}
      </div>

      {/* iter234h — sticky bulk-action toolbar */}
      {selected.size > 0 && (
        <BulkActionToolbar
          count={selected.size}
          visibleCount={visible.length}
          unreadyVisible={visible.filter(b => !(b.map_status?.is_on_map)).length}
          onSelectUnready={selectVisibleNotReady}
          onClear={clearSelection}
          onApply={applyBulkPatch}
          running={bulkRunning}
        />
      )}
      {selected.size === 0 && visible.some(b => !(b.map_status?.is_on_map)) && (
        <div data-testid="bulk-action-quick-pick" style={{
          position: 'fixed', right: 22, bottom: 22, zIndex: 40,
          background: 'rgba(15,11,26,0.94)',
          border: `1px solid ${GOLD}`, borderRadius: 999,
          padding: '10px 18px', fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13, color: IVORY,
          boxShadow: '0 14px 38px rgba(0,0,0,0.5)',
          cursor: 'pointer', backdropFilter: 'blur(6px)',
        }} onClick={selectVisibleNotReady}>
          ✦ Select {visible.filter(b => !(b.map_status?.is_on_map)).length} not-yet-pinned bookings
        </div>
      )}

      {editing && (
        <BookingEditModal
          key={editing === 'new' ? 'new' : editing.booking_id}
          booking={editing === 'new' ? null : editing}
          inventory={inventory}
          packages={packages}
          onClose={() => setEditing(null)}
          onSave={saveBooking}
        />
      )}

      {viewingQuote && (
        <QuoteViewerModal
          quoteId={viewingQuote}
          onClose={() => setViewingQuote(null)}
          onChange={loadAll}
        />
      )}
    </div>
  );
};

const pageStyle = {
  minHeight: '100vh', background: '#0F0B1A', color: IVORY, fontFamily: SERIF,
  padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
};

const eyebrowStyle = {
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
  color: GOLD, marginBottom: 8,
};

const primaryPillStyle = {
  padding: '9px 18px', borderRadius: 999,
  border: `1px solid ${GOLD}`,
  background: 'linear-gradient(135deg, rgba(212,170,82,0.22), rgba(184,151,98,0.12))',
  color: IVORY, fontFamily: SERIF, fontSize: 13, letterSpacing: '0.04em', cursor: 'pointer',
};

const ghostPillStyle = {
  padding: '9px 18px', borderRadius: 999,
  background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY_DIM, fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 13, cursor: 'pointer',
};

const FilterPill = ({ active, children, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    style={{
      padding: '6px 14px', borderRadius: 999,
      border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.14)'}`,
      background: active ? 'rgba(212,170,82,0.18)' : 'rgba(255,255,255,0.04)',
      color: active ? GOLD : IVORY_DIM,
      fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, cursor: 'pointer',
    }}>{children}</button>
);

const BulkActionToolbar = ({ count, visibleCount, unreadyVisible, onSelectUnready, onClear, onApply, running }) => (
  <div data-testid="bulk-action-toolbar" style={{
    position: 'fixed', left: '50%', bottom: 22, transform: 'translateX(-50%)',
    zIndex: 50, background: 'rgba(15,11,26,0.96)',
    border: `1px solid ${GOLD}`, borderRadius: 18,
    padding: '14px 22px', display: 'flex', gap: 14, alignItems: 'center',
    flexWrap: 'wrap', maxWidth: '95vw',
    boxShadow: '0 20px 50px rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
  }}>
    <span style={{
      fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
      color: IVORY, letterSpacing: '0.02em',
    }}>
      <strong style={{ color: GOLD, fontStyle: 'normal' }}>{count}</strong> selected
    </span>
    {unreadyVisible > count && (
      <button type="button" onClick={onSelectUnready}
        data-testid="bulk-select-unready"
        style={ghostPillSmall}>
        Select all {unreadyVisible} not-yet-pinned
      </button>
    )}
    <button type="button"
      disabled={running}
      onClick={() => onApply(
        { status: 'completed', is_public_event: true },
        'Mark public + set Completed',
      )}
      data-testid="bulk-mark-public-complete"
      style={{
        ...primaryPillSmall,
        opacity: running ? 0.5 : 1,
        cursor: running ? 'wait' : 'pointer',
      }}>
      ✦ Mark public + Complete
    </button>
    <button type="button"
      disabled={running}
      onClick={() => onApply(
        { is_public_event: true },
        'Mark as public event',
      )}
      data-testid="bulk-mark-public"
      style={ghostPillSmall}>
      Mark as public
    </button>
    <button type="button"
      disabled={running}
      onClick={() => onApply({ status: 'completed' }, 'Set status = Completed')}
      data-testid="bulk-mark-completed"
      style={ghostPillSmall}>
      Set Completed
    </button>
    <button type="button" onClick={onClear} data-testid="bulk-clear"
      style={{
        ...ghostPillSmall, borderColor: 'rgba(227,154,79,0.5)',
        color: '#E39A4F',
      }}>
      Clear
    </button>
  </div>
);

const ghostPillSmall = {
  padding: '7px 14px', borderRadius: 999,
  border: `1px solid ${GOLD_FAINT}`, background: 'transparent',
  color: IVORY, fontFamily: SERIF, fontSize: 12.5,
  fontStyle: 'italic', cursor: 'pointer', whiteSpace: 'nowrap',
  letterSpacing: '0.02em',
};
const primaryPillSmall = {
  padding: '7px 16px', borderRadius: 999,
  border: `1px solid ${GOLD}`,
  background: 'linear-gradient(135deg, rgba(212,170,82,0.28), rgba(184,151,98,0.12))',
  color: IVORY, fontFamily: SERIF, fontSize: 12.5,
  fontStyle: 'italic', cursor: 'pointer', whiteSpace: 'nowrap',
  letterSpacing: '0.02em',
};

const MapStatusBadge = ({ booking, onEdit }) => {
  // iter234g/k — answers Craig's question "what does this booking
  // need to drop a pin + show a photo?" right on the row. Reads the
  // server-derived booking.map_status; falls back to local heuristics
  // if the field isn't there (older deployments). iter234k: now also
  // shows the FIRST missing item inline (mobile-friendly — no hover
  // needed) and tapping the badge opens the booking edit modal so
  // Craig can fix it in two taps.
  const ms = booking.map_status || (() => {
    const missing = [];
    if (!booking.postcode) missing.push('postcode');
    if (!booking.is_public_event) missing.push('mark as public event');
    if (booking.status !== 'completed') missing.push('set status to completed');
    return {
      is_on_map: false, has_hero_image: false, missing,
      next_step: missing[0] || 'save booking',
    };
  })();

  // iter234l — human-readable, action-led labels for each missing
  // gate, so the badge reads "⚠ Tap to add postcode" not the
  // computer-spec "⚠ Needs: postcode".
  const HUMAN_FIX = {
    'postcode': 'Tap to add postcode',
    'mark as public event': 'Tap to tick "Public event"',
    'set status to completed': 'Tap to set status Completed',
    'save booking to trigger promotion': 'Tap Save to drop the pin',
    "re-geocode (postcode wasn't resolvable)": 'Tap to re-check postcode',
    'hero photo on the linked event': 'Add hero photo on /admin/historic-events',
  };
  const friendly = (s) => HUMAN_FIX[s] || `Fix: ${s}`;

  const ready = ms.is_on_map && ms.has_hero_image;
  const pinOnly = ms.is_on_map && !ms.has_hero_image;
  const stops = ms.missing || [];
  const tip = ready
    ? 'On the map with a hero photo ✦'
    : pinOnly
      ? 'On the map. Add a hero photo on /admin/historic-events for a thumbnail pin.'
      : `Still needs: ${stops.map(friendly).join(' · ')}`;

  const bg = ready ? 'rgba(126,192,128,0.18)'
    : pinOnly ? 'rgba(212,170,82,0.18)'
    : 'rgba(220,80,80,0.16)';  // iter234l: properly RED for blockers
  const color = ready ? '#9ED39E'
    : pinOnly ? '#D4AA52'
    : '#E26060';  // iter234l: proper red
  // iter234l: action-led label
  const label = ready ? '✓ Pinned · Photo'
    : pinOnly ? '◐ Pinned · No photo'
    : stops.length === 1
      ? `⚠ ${friendly(stops[0])}`
      : `⚠ ${friendly(stops[0])} · +${stops.length - 1} more`;

  const handleClick = (e) => {
    e.stopPropagation();
    if (!ready && onEdit) onEdit();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={tip}
      data-testid={`booking-map-status-${booking.booking_id}`}
      data-on-map={ready || pinOnly}
      data-missing-count={stops.length}
      style={{
        display: 'inline-block', padding: '4px 10px', borderRadius: 999,
        background: bg, color, fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11.5, whiteSpace: 'nowrap', letterSpacing: '0.02em',
        border: `1px solid ${color}40`,
        cursor: ready ? 'default' : 'pointer',
        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{label}</button>
  );
};

const BookingRow = ({ booking, packages, quoteId, selected, onToggleSelect, onEdit, onDelete, onStatusChange, onSendDeposit, onMarkConfirmed, onQuote, onPublishEvent }) => {
  const pkg = packages.find(p => p.slug === booking.package_slug);
  const status = STATUS_STYLE[booking.status] || STATUS_STYLE.pending;
  // iter225 — contextual primary action based on current state.
  // pending → "Send Deposit Request" · awaiting_deposit → "Mark Confirmed" ·
  // confirmed/completed/cancelled/expired → none (use the dropdown).
  const primaryAction = booking.status === 'pending'
    ? { label: 'Send Deposit Request ✦', fn: onSendDeposit, testid: `booking-send-deposit-${booking.booking_id}` }
    : booking.status === 'awaiting_deposit'
      ? { label: 'Mark Confirmed ✦', fn: onMarkConfirmed, testid: `booking-mark-confirmed-${booking.booking_id}` }
      : null;
  return (
    <div data-testid={`booking-row-${booking.booking_id}`}
      data-status={booking.status}
      data-selected={selected ? 'true' : 'false'}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(110px, 130px) minmax(180px, 1.6fr) minmax(150px, 1fr) auto auto auto auto auto auto',
        alignItems: 'center', gap: 14, padding: '14px 18px',
        background: selected ? 'rgba(212,170,82,0.10)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${selected ? GOLD : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 12,
        transition: 'background 0.18s ease, border-color 0.18s ease',
      }}>
      <label data-testid={`booking-select-${booking.booking_id}`}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={!!selected}
          onChange={onToggleSelect}
          style={{ width: 17, height: 17, accentColor: GOLD, cursor: 'pointer' }} />
      </label>
      <div>
        <div style={{ fontFamily: SERIF, fontSize: 13.5, color: IVORY, fontWeight: 500 }}>
          {prettyDate(booking.event_date)}
        </div>
        {booking.deposit_due_date && booking.status === 'awaiting_deposit' && (
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, color: 'rgba(227,154,79,0.95)', marginTop: 2 }}>
            Deposit due {prettyDate(booking.deposit_due_date)}
          </div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: SERIF, fontSize: 14.5, color: IVORY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {booking.customer_name || '(unnamed)'}
        </div>
        <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_DIM, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {booking.customer_email}{booking.venue ? ` · ${booking.venue}` : ''}
        </div>
      </div>
      <div>
        <div style={{ fontFamily: SERIF, fontSize: 12.5, color: IVORY_DIM, fontStyle: 'italic' }}>
          {pkg ? pkg.name : booking.package_slug || '(no package)'}
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 11.5, color: IVORY_GHOST, marginTop: 2, letterSpacing: '0.02em' }}>
          {booking.equipment.length === 0
            ? 'No equipment selected'
            : `${booking.equipment.length} item${booking.equipment.length === 1 ? '' : 's'}`}
          {booking.deposit_amount ? ` · £${booking.deposit_amount} deposit` : ''}
        </div>
      </div>
      <select
        value={booking.status}
        onChange={(e) => onStatusChange(e.target.value)}
        data-testid={`booking-status-${booking.booking_id}`}
        style={{
          padding: '6px 10px', borderRadius: 999,
          background: status.tint, color: status.text,
          border: `1px solid ${status.tint}`,
          fontFamily: SERIF, fontSize: 12, fontStyle: 'italic',
          cursor: 'pointer', letterSpacing: '0.04em',
        }}>
        {STATUSES.map(s => (
          <option key={s.key} value={s.key} style={{ background: '#0F0B1A', color: IVORY }}>{s.label}</option>
        ))}
      </select>
      {primaryAction ? (
        <button type="button" onClick={primaryAction.fn} data-testid={primaryAction.testid}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: `1px solid ${GOLD}`,
            background: 'linear-gradient(135deg, rgba(212,170,82,0.22), rgba(184,151,98,0.10))',
            color: IVORY, fontFamily: SERIF, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.02em', whiteSpace: 'nowrap',
          }}>{primaryAction.label}</button>
      ) : <span />}
      <button type="button" onClick={onQuote} data-testid={`booking-quote-${booking.booking_id}`}
        style={{
          padding: '6px 14px', borderRadius: 999,
          border: `1px solid ${quoteId ? GOLD : GOLD_FAINT}`,
          background: quoteId ? 'rgba(212,170,82,0.16)' : 'transparent',
          color: quoteId ? GOLD : IVORY_DIM,
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, cursor: 'pointer',
          letterSpacing: '0.02em', whiteSpace: 'nowrap',
        }}>{quoteId ? 'View Quote ✦' : 'Generate Quote ✦'}</button>
      <MapStatusBadge booking={booking} onEdit={onEdit} />
      {/* iter232 — one-click publish from a booking: imports venue,
          customer, package, date, postcode automatically, then
          drops Craig into the Event Publisher to add photos. */}
      {booking.status === 'completed' && !booking.promoted_event_slug && (
        <button type="button" onClick={onPublishEvent}
          data-testid={`booking-publish-event-${booking.booking_id}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: `1px solid ${GOLD}`,
            background: 'linear-gradient(135deg, rgba(212,170,82,0.22), rgba(184,151,98,0.10))',
            color: GOLD, fontFamily: SERIF, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.02em', whiteSpace: 'nowrap',
          }}>✦ Publish Event</button>
      )}
      <button type="button" onClick={onEdit} data-testid={`booking-edit-${booking.booking_id}`}
        style={{ ...ghostPillStyle, padding: '6px 14px', fontSize: 12.5 }}>Edit</button>
      <ReviewLinkButton booking={booking} />
      <button type="button" onClick={onDelete} data-testid={`booking-delete-${booking.booking_id}`}
        style={{ background: 'transparent', border: 'none', color: 'rgba(184,111,111,0.78)',
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, cursor: 'pointer' }}>Delete</button>
    </div>
  );
};

// iter229 — Pre-event reminder controls. Surfaces which waves have
// fired, lets the operator pause all five for a given booking, and
// exposes a one-tap manual send for each individual wave.
const REMINDER_WAVES = [
  { key: 't_minus_30d', label: '1 month before',  days: 30 },
  { key: 't_minus_14d', label: '2 weeks before',  days: 14 },
  { key: 't_minus_7d',  label: '1 week before',   days: 7 },
  { key: 't_minus_2d',  label: '2 days before',   days: 2 },
  { key: 't_minus_1d',  label: '1 day before',    days: 1 },
];

const ReminderControls = ({ booking, form, setForm }) => {
  const sent = new Set(booking?.reminders_sent || []);
  const isConfirmed = ['confirmed', 'completed'].includes(booking?.status);
  const togglePaused = () => setForm((f) => ({ ...f, reminders_paused: !f.reminders_paused }));

  const sendWave = async (waveKey) => {
    if (!isConfirmed) {
      toast.error('Reminders only fire on confirmed bookings.');
      return;
    }
    if (!window.confirm(`Send the "${REMINDER_WAVES.find(w => w.key === waveKey).label}" reminder now?\n\nThe customer and hello@eventdrop.co will both receive a copy.`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/bookings/${booking.booking_id}/send-reminder`,
        null,
        { params: { wave: waveKey } },
      );
      if (r.data?.email_sent === false) {
        toast.error('Reminder send reported a delivery failure — check Resend logs.');
      } else {
        toast.success('Reminder sent.');
      }
      // Local optimistic update so the UI badge flips to "sent" without a reload.
      sent.add(waveKey);
      setForm((f) => ({ ...f })); // force re-render via state nudge
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    }
  };

  return (
    <div data-testid="be-reminders-panel"
      style={{ borderTop: `1px solid ${GOLD_HAIRLINE}`, paddingTop: 18, marginTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 4 }}>
            ✦ Pre-event reminders
          </div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: IVORY_DIM, maxWidth: 480, lineHeight: 1.5 }}>
            Five calm nudges to the customer (and a copy to <span style={{ color: IVORY }}>hello@eventdrop.co</span>) at 1 month, 2 weeks, 1 week, 2 days and 1 day before the event. Fires only on <span style={{ color: GOLD }}>Confirmed</span> bookings.
          </div>
        </div>
        <label data-testid="be-reminders-pause-toggle"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                   fontFamily: SERIF, fontSize: 13, color: form.reminders_paused ? '#E6B5B5' : IVORY }}>
          <input type="checkbox" checked={!!form.reminders_paused} onChange={togglePaused}
            style={{ accentColor: GOLD, width: 16, height: 16 }} />
          Pause reminders for this booking
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        {REMINDER_WAVES.map(w => {
          const hasFired = sent.has(w.key);
          return (
            <div key={w.key}
              data-testid={`reminder-wave-${w.key}`}
              style={{
                border: `1px solid ${hasFired ? GOLD_FAINT : GOLD_HAIRLINE}`,
                background: hasFired ? 'rgba(212,170,82,0.08)' : 'transparent',
                borderRadius: 6, padding: '10px 12px',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
              <div style={{ fontFamily: SERIF, fontSize: 13, color: IVORY }}>{w.label}</div>
              <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5,
                            color: hasFired ? GOLD : IVORY_GHOST }}>
                {hasFired ? '✓ sent' : (form.reminders_paused ? 'paused' : 'pending')}
              </div>
              <button type="button"
                data-testid={`reminder-send-${w.key}`}
                onClick={() => sendWave(w.key)}
                disabled={!isConfirmed}
                style={{
                  marginTop: 4,
                  border: `1px solid ${GOLD_HAIRLINE}`,
                  background: 'transparent', color: isConfirmed ? IVORY : IVORY_GHOST,
                  borderRadius: 999, padding: '4px 10px', fontFamily: SERIF, fontSize: 11.5,
                  cursor: isConfirmed ? 'pointer' : 'not-allowed',
                }}>
                {hasFired ? 'Re-send' : 'Send now'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};



const BookingEditModal = ({ booking, inventory, packages, onClose, onSave }) => {
  const isNew = !booking;
  const [form, setForm] = useState({
    customer_name: booking?.customer_name || '',
    customer_email: booking?.customer_email || '',
    customer_phone: booking?.customer_phone || '',
    event_date: booking?.event_date || '',
    package_slug: booking?.package_slug || '',
    equipment: booking?.equipment || [],
    venue: booking?.venue || '',
    postcode: booking?.postcode || '',
    notes: booking?.notes || '',
    status: booking?.status || 'pending',
    deposit_amount: booking?.deposit_amount ?? '',
    deposit_due_date: booking?.deposit_due_date || '',
    payment_instructions: booking?.payment_instructions || '',
    travel_miles: booking?.travel_miles ?? '',
    reminders_paused: !!booking?.reminders_paused,
    // iter231e — opt-out of auto-promotion to public map
    is_public_event: booking?.is_public_event !== false,
  });

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleEquip = (slug) => {
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(slug)
        ? f.equipment.filter(s => s !== slug)
        : [...f.equipment, slug],
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim() || !form.event_date) {
      toast.error('Name, email and event date are required.');
      return;
    }
    // Normalise deposit_amount (empty string → null, else integer).
    const payload = {
      ...form,
      deposit_amount: form.deposit_amount === '' || form.deposit_amount === null
        ? null
        : Number(form.deposit_amount),
      deposit_due_date: form.deposit_due_date || null,
      travel_miles: form.travel_miles === '' || form.travel_miles === null
        ? 0
        : Number(form.travel_miles),
    };
    onSave(payload, booking?.booking_id);
  };

  return (
    <div
      data-testid="booking-edit-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(8,5,3,0.78)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 'clamp(20px, 4vw, 50px) clamp(16px, 4vw, 40px)',
        overflowY: 'auto',
      }}>
      <form onSubmit={submit} style={{
        width: '100%', maxWidth: 640,
        background: '#1A1325',
        border: `1px solid ${GOLD_FAINT}`,
        borderRadius: 14,
        padding: 'clamp(22px, 4vw, 36px)',
        color: IVORY,
      }}>
        <div style={{ ...eyebrowStyle, textAlign: 'center', marginBottom: 10 }}>
          ✦ {isNew ? 'New booking' : 'Edit booking'}
        </div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: '0 0 22px', textAlign: 'center' }}>
          {isNew ? 'Capture a new booking' : booking.customer_name || 'Update booking'}
        </h2>

        {/* iter234l — pin-readiness banner so Craig sees EXACTLY what
            needs fixing for the pin to drop. Computed live from the
            current form state so it updates as he types. */}
        {(() => {
          const missing = [];
          if (!(form.postcode || '').trim()) missing.push({ key: 'postcode', label: 'Postcode is empty — needed to geocode the pin' });
          if (!form.is_public_event) missing.push({ key: 'is_public_event', label: 'Public-event toggle is off — tick it to allow the pin to drop' });
          if (form.status !== 'completed') missing.push({ key: 'status', label: `Status is "${form.status}" — must be "Completed" for the pin to drop` });
          if (missing.length === 0) {
            return (
              <div data-testid="be-pin-readiness-ok" style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 12,
                background: 'rgba(126,192,128,0.12)',
                border: '1px solid rgba(126,192,128,0.45)',
                color: '#9ED39E', fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13, letterSpacing: '0.02em',
              }}>
                ✓ This booking will appear on the map as soon as you save.
              </div>
            );
          }
          return (
            <div data-testid="be-pin-readiness-warn" style={{
              marginBottom: 16, padding: '12px 16px', borderRadius: 12,
              background: 'rgba(220,80,80,0.10)',
              border: '1px solid #E26060',
              color: '#FFD9D9', fontFamily: SERIF,
            }}>
              <div style={{
                fontStyle: 'italic', fontSize: 12, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: '#E26060', marginBottom: 6,
              }}>⚠ Pin not dropping — fix the highlighted field{missing.length > 1 ? 's' : ''}</div>
              <ol style={{ margin: 0, paddingLeft: 22, fontSize: 13.5,
                            lineHeight: 1.55, color: '#FFE0E0' }}>
                {missing.map(m => <li key={m.key}>{m.label}</li>)}
              </ol>
            </div>
          );
        })()}

        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <div>
              <label style={labelStyle}>Customer name *</label>
              <input value={form.customer_name} onChange={upd('customer_name')} style={inputBase} data-testid="be-name" />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={form.customer_email} onChange={upd('customer_email')} style={inputBase} data-testid="be-email" />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.customer_phone} onChange={upd('customer_phone')} style={inputBase} data-testid="be-phone" />
            </div>
            <div>
              <label style={labelStyle}>Event date *</label>
              <input type="date" value={form.event_date} onChange={upd('event_date')} style={{ ...inputBase, colorScheme: 'dark' }} data-testid="be-date" />
            </div>
            <div>
              <label style={labelStyle}>Package</label>
              <select value={form.package_slug} onChange={upd('package_slug')} style={inputBase} data-testid="be-package">
                <option value="">(none)</option>
                {packages.map(p => <option key={p.slug} value={p.slug}>{p.name} — £{p.price}</option>)}
              </select>
            </div>
            <div>
              <label style={form.status !== 'completed' ? { ...labelStyle, color: '#E26060' } : labelStyle}>
                Status {form.status !== 'completed' && <span style={{ color: '#E26060', fontStyle: 'italic' }}>· must be Completed for pin to drop</span>}
              </label>
              <select value={form.status} onChange={upd('status')}
                style={form.status !== 'completed'
                  ? { ...inputBase, borderColor: '#E26060', boxShadow: '0 0 0 2px rgba(226,96,96,0.18)' }
                  : inputBase}
                data-testid="be-status">
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Venue</label>
              <input value={form.venue} onChange={upd('venue')} style={inputBase} data-testid="be-venue" />
            </div>
            <div>
              <label style={!(form.postcode || '').trim() ? { ...labelStyle, color: '#E26060' } : labelStyle}>
                Postcode {!(form.postcode || '').trim()
                  ? <span style={{ color: '#E26060', fontStyle: 'italic' }}>· required for the pin to drop</span>
                  : <span style={{ fontSize: 10.5, color: GOLD, fontStyle: 'italic' }}>· auto-geocoded for map</span>}
              </label>
              <input
                value={form.postcode || ''}
                onChange={upd('postcode')}
                placeholder="e.g. LL12 0PB"
                style={!(form.postcode || '').trim()
                  ? { ...inputBase, borderColor: '#E26060', boxShadow: '0 0 0 2px rgba(226,96,96,0.18)' }
                  : inputBase}
                data-testid="be-postcode"
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Equipment booked</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
              {inventory.filter(i => i.is_active).map((it) => {
                const chosen = form.equipment.includes(it.slug);
                return (
                  <label key={it.slug}
                    data-testid={`be-equip-${it.slug}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 12px', borderRadius: 10,
                      background: chosen ? 'rgba(212,170,82,0.16)' : 'rgba(0,0,0,0.18)',
                      border: `1px solid ${chosen ? GOLD : 'rgba(255,255,255,0.08)'}`,
                      cursor: 'pointer',
                    }}>
                    <input type="checkbox"
                      checked={chosen}
                      onChange={() => toggleEquip(it.slug)}
                      style={{ accentColor: GOLD }} />
                    <span style={{ fontFamily: SERIF, fontSize: 13.5, color: IVORY }}>{it.name}</span>
                    <span style={{ marginLeft: 'auto', fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: IVORY_GHOST }}>
                      stock {it.total_stock}
                    </span>
                  </label>
                );
              })}
            </div>
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: IVORY_GHOST, marginTop: 6 }}>
              Selected items are deducted from availability on <strong style={{ color: GOLD }}>{form.event_date || 'this date'}</strong> once the booking is Confirmed.
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={upd('notes')}
              rows={4} style={{ ...inputBase, resize: 'vertical', minHeight: 80 }} data-testid="be-notes" />
          </div>

          {/* iter225 — deposit metadata. These show on the deposit
              request email and (where set) on the booking row in
              the list. Empty values are fine — the system applies
              sensible defaults (7-day due date) when the deposit
              request button is clicked. */}
          <div style={{
            padding: '18px 18px', borderRadius: 12,
            background: 'rgba(212,170,82,0.04)',
            border: `1px dashed ${GOLD_FAINT}`,
            display: 'grid', gap: 12,
          }}>
            <div style={{ ...eyebrowStyle, marginBottom: 0 }}>✦ Deposit details</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div>
                <label style={labelStyle}>Deposit amount (£)</label>
                <input type="number" min="0" max="999999"
                  value={form.deposit_amount}
                  onChange={upd('deposit_amount')}
                  data-testid="be-deposit-amount"
                  placeholder="e.g. 100"
                  style={inputBase} />
              </div>
              <div>
                <label style={labelStyle}>Deposit due by</label>
                <input type="date"
                  value={form.deposit_due_date}
                  onChange={upd('deposit_due_date')}
                  data-testid="be-deposit-due"
                  style={{ ...inputBase, colorScheme: 'dark' }} />
              </div>
              <div>
                <label style={labelStyle}>Travel miles (from Wrexham)</label>
                <input type="number" min="0" max="9999"
                  value={form.travel_miles}
                  onChange={upd('travel_miles')}
                  data-testid="be-travel-miles"
                  placeholder="e.g. 60"
                  style={inputBase} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Payment instructions (sent to customer)</label>
              <textarea value={form.payment_instructions} onChange={upd('payment_instructions')}
                rows={4}
                placeholder="e.g. Bank transfer to EventDrop Ltd · sort code 00-00-00 · account 12345678 · reference: your event date"
                style={{ ...inputBase, resize: 'vertical', minHeight: 80 }}
                data-testid="be-payment-instructions" />
              <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: IVORY_GHOST, marginTop: 4 }}>
                These appear verbatim in the deposit request email. Leave blank to send a &quot;reply for details&quot; template.
              </div>
            </div>
            {!isNew && (
              <ReminderControls booking={booking} form={form} setForm={setForm} />
            )}
            {/* iter231e + iter234l — Auto-promote toggle. Red highlight when off so Craig sees exactly what to tick. */}
            <div style={{
              border: form.is_public_event
                ? `1px solid ${GOLD_FAINT}`
                : '2px solid #E26060',
              background: form.is_public_event
                ? 'rgba(212,170,82,0.07)'
                : 'rgba(220,80,80,0.10)',
              borderRadius: 8, padding: '14px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: 14, flexWrap: 'wrap',
              boxShadow: form.is_public_event ? 'none' : '0 0 0 2px rgba(226,96,96,0.18)',
            }} data-testid="be-public-event-row">
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontFamily: SERIF, fontSize: 14,
                              color: form.is_public_event ? IVORY : '#FFD9D9',
                              marginBottom: 4 }}>
                  ✦ Public portfolio event
                  {!form.is_public_event && (
                    <span style={{ color: '#E26060', fontStyle: 'italic',
                                    fontSize: 12, marginLeft: 8 }}>
                      · tick this to drop the pin
                    </span>
                  )}
                </div>
                <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
                              color: form.is_public_event ? IVORY_DIM : '#FFE0E0',
                              lineHeight: 1.5 }}>
                  When this booking flips to <span style={{ color: GOLD }}>Completed</span> it auto-drops a pin onto the map and grows venue + package stats. Untick for privacy-sensitive events.
                </div>
              </div>
              <label data-testid="be-public-event-toggle"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                         fontFamily: SERIF, fontSize: 13,
                         color: form.is_public_event ? IVORY : '#FFD9D9' }}>
                <input type="checkbox" checked={!!form.is_public_event}
                  onChange={(e) => setForm({ ...form, is_public_event: e.target.checked })}
                  style={{ accentColor: form.is_public_event ? GOLD : '#E26060', width: 18, height: 18 }} />
                Auto-promote to map
              </label>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24, flexWrap: 'wrap' }}>
          <button type="button" onClick={onClose} style={ghostPillStyle}>Cancel</button>
          <button type="submit" data-testid="be-save" style={primaryPillStyle}>
            {isNew ? 'Create booking ✦' : 'Save changes ✦'}
          </button>
        </div>
      </form>
    </div>
  );
};

// iter228 — Review link generator per booking. Surfaces:
//   • Copy customer-facing review link (prefilled from booking row)
//   • Download QR code PNG (printable in invoices / thank-you cards)
//   • Copy SMS template
//   • Copy email template (subject + body)
const ReviewLinkButton = ({ booking }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const openSheet = async () => {
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bookings/${booking.booking_id}/review-link`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate review link.');
      setOpen(false);
    } finally { setLoading(false); }
  };

  const copy = (text, label) => {
    if (!navigator.clipboard) { toast.error('Clipboard unavailable.'); return; }
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied ✦`);
  };

  return (
    <>
      <button type="button" onClick={openSheet} data-testid={`booking-review-link-${booking.booking_id}`}
        style={{ ...ghostPillStyle, padding: '6px 14px', fontSize: 12.5 }}>
        ★ Review link
      </button>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,11,9,0.78)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20, zIndex: 1000,
        }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="review-link-modal"
            style={{
              background: '#1A120A', borderRadius: 14, padding: '28px 30px',
              maxWidth: 580, width: '100%',
              border: `1px solid ${GOLD_FAINT}`, color: IVORY, fontFamily: SERIF,
              maxHeight: '90vh', overflowY: 'auto',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
              <div>
                <div style={{ fontStyle: 'italic', fontSize: 11.5, letterSpacing: '0.28em',
                  textTransform: 'uppercase', color: GOLD, marginBottom: 4 }}>✦ Customer review</div>
                <h3 style={{ margin: 0, fontWeight: 500, fontSize: 22 }}>
                  Share with {booking.customer_name?.split(' ')[0] || 'customer'}
                </h3>
              </div>
              <button type="button" onClick={() => setOpen(false)}
                style={{ background: 'transparent', border: 'none', color: IVORY_DIM,
                  fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            {loading || !data ? (
              <p style={{ color: IVORY_DIM, fontStyle: 'italic' }}>Generating link…</p>
            ) : (
              <>
                {data.review_submitted_at && (
                  <div style={{
                    background: 'rgba(125,181,140,0.16)', color: '#CFEFC6',
                    padding: '8px 12px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                  }}>
                    ✓ Customer has already submitted a review on {data.review_submitted_at.slice(0, 10)}.
                  </div>
                )}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11.5, color: IVORY_DIM, fontStyle: 'italic', marginBottom: 4 }}>Review link</div>
                  <code style={{
                    display: 'block', background: 'rgba(0,0,0,0.4)', padding: '10px 12px',
                    borderRadius: 6, fontSize: 12.5, color: IVORY, wordBreak: 'break-all',
                  }} data-testid="review-link-url">{data.link}</code>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
                  <img src={data.qr_data_uri} alt="Review QR code"
                    data-testid="review-link-qr"
                    style={{ width: 130, height: 130, background: '#FFF', borderRadius: 8 }} />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ color: IVORY_DIM, fontStyle: 'italic', fontSize: 13, lineHeight: 1.55, margin: '0 0 12px' }}>
                      Print this QR onto invoices, thank-you cards, or place in the guest memory box —
                      customers scan it to leave a pre-filled review.
                    </p>
                    <a href={`${API}/admin/bookings/${booking.booking_id}/review-qr.png`}
                      data-testid="review-link-download-qr"
                      style={{ ...primaryPillStyle, textDecoration: 'none', fontSize: 12.5, padding: '8px 14px' }}>
                      ⬇ Download QR (PNG)
                    </a>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => copy(data.link, 'Review link')}
                    data-testid="review-link-copy" style={ghostPillStyle}>📋 Copy link</button>
                  <button type="button" onClick={() => copy(data.templates.sms_body, 'SMS message')}
                    data-testid="review-link-copy-sms" style={ghostPillStyle}>📱 Copy SMS</button>
                  <button type="button" onClick={() => copy(
                      `Subject: ${data.templates.email_subject}\n\n${data.templates.email_body}`, 'Email')}
                    data-testid="review-link-copy-email" style={ghostPillStyle}>📧 Copy email</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};



export default AdminBookingsPage;
